// ViewDetailsPopup.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Row, Col } from '@themesberg/react-bootstrap';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import API from '../utils/api';

const excludedKeys = ['_id', 'user', '__v', 'createdAt', 'updatedAt', 'notes', "aiExtractedVariables", 'paymentStatusUpdatedAt' , 'paymentStatus', "Check", 'PenaltyCharge', 'SeverityLevel', "AddressSearch"];

const ViewDetailsPopup = ({ show, onClose, claimId, isAdmin = false }) => {
  const { t } = useTranslation();
const paymentStatusMapping = (t) => ({
  "Waiting for Admin Action": { bg: "warning", text: "dark", label: t("statuses.waitingAdminAction.value") },
  "In Process": { bg: "primary", label: t("Approve") },
  "Closed": { bg: "success", label: t("fixedStatuses.completed") },
  "Rejected": { bg: "danger", label: t("RejectedClaim") },
  "debtor has paid, payment coming soon": { bg: "#28a745", custom: true, label: t("fixedStatuses.paid") },
  "Installment payment agreement in place": { bg: "#17a2b8", custom: true, label: t("fixedStatuses.installmentPayment") },
  "In Progress": { bg: "#ffc107", custom: true, text: "#212529", label: t("fixedStatuses.debtCollectionInitiated") },
  "court dunning procedure": { bg: "#fd7e14", custom: true, label: t("fixedStatuses.courtDunningProcedure") },
  "we contact the debtor": { bg: "#6610f2", custom: true, label: t("fixedStatuses.resubmissions") },
  "enforcement order obtained": { bg: "#dc3545", custom: true, label: t("fixedStatuses.foreclosures") },
  "case closed": { bg: "#20c997", custom: true, label: t("fixedStatuses.completed") },
  "cancellation": { bg: "#6c757d", custom: true, label: t("fixedStatuses.cancellation") },
});

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);



  useEffect(() => {
    if (!show || !claimId) return;

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await API.get(`/claims/getsingle/${claimId}`);
        setDetails(response.data);
      } catch (err) {
        setError(t('viewDetailsPopupTranslations.messages.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, claimId, t]);
  const fieldLabels = {
    fileReferenceNo: t('viewPopupFileReferenceNoLabel'),
    document: t('viewDetailsPopupTranslations.labels.document'),
    documentOptional: t('viewDetailsPopupTranslations.labels.documentOptional'),
    document1: t('viewDetailsPopupTranslations.labels.document1'),
    name: t('viewDetailsPopupTranslations.labels.name'),
    email: t('viewDetailsPopupTranslations.labels.email'),
    description: t('viewDetailsPopupTranslations.labels.description'),
    status: t('viewDetailsPopupTranslations.labels.status'),
    rejectionReason: t('viewDetailsPopupTranslations.labels.rejectionReason'),
    generatedDocument: t('viewDetailsPopupTranslations.labels.generatedDocument'),
    NoOfDays: t('viewDetailsPopupTranslations.labels.NoOfDays'),
    SeverityLevel: t("SeverityLevel"),
    address: t("address"),
    claimType: t("claimType"),
    companyName: t("companyName"),
    paymentStatus: t("paymentStatus"),
    AboutInvoice: t("AboutInvoice")
  };
  const renderFieldValue = (key, value) => {
    if (!value) return <span className="text-muted">{t('viewDetailsPopupTranslations.values.na')}</span>;

    if (typeof value === 'string' && value.toLowerCase().endsWith('.pdf')) {
      const url = value.startsWith('http')
        ? value
        : `https://api-dashboard.nordmann-inkasso.de
/${value.replace(/\\/g, '/')}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="d-flex align-items-center text-decoration-none"
        >
          <FontAwesomeIcon icon={faFilePdf} className="me-2 text-danger" />
          {t('viewDetailsPopupTranslations.buttons.downloadPdf')}
        </a>
      );
    }

    if (key.toLowerCase().includes('date')) {
      const dateObj = new Date(value);
      return dateObj.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

   // Handle paymentStatus mapping
// Handle status and paymentStatus mapping
if (key === "paymentStatus" || key === "status") {
  const mapping = paymentStatusMapping(t);
  const matchedStatus = mapping[value];

  if (matchedStatus) {
    const { bg, text, label } = matchedStatus;
    return (
      <span
        className={`badge bg-${bg} ${text ? `text-${text}` : ""}`}
        style={
          matchedStatus.custom
            ? { backgroundColor: bg, color: text || "#fff" }
            : {}
        }
      >
        {label}
      </span>
    );
  }

  // fallback if no mapping found — still translate plain value if exists in i18n
  const translatedValue = t(`fixedStatuses.${value}`, value);
  return <span>{translatedValue}</span>;
}



    return <span>{value}</span>;
  };

  let filteredEntries = [];
  if (details) {
    if (isAdmin) {
      filteredEntries = Object.entries(details).filter(
        ([key, value]) =>
          !excludedKeys.includes(key) &&
          value !== '' &&
          value !== null &&
          value !== undefined &&
          key !== 'mandateSkipped'
      );
    } else {
      const clientKeys = ['fileReferenceNo', 'status', 'rejectionReason'];
      filteredEntries = Object.entries(details).filter(([key, value]) => {
        if (value === '' || value === null || value === undefined) return false;
        if (clientKeys.includes(key)) return true;
        if (key.startsWith('document') && key !== 'generatedDocument') return true;
        return false;
      });
    }
  }
  console.log("filteredEntries", filteredEntries);
  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('viewDetailsPopupTranslations.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">{t('viewDetailsPopupTranslations.messages.loading')}</p>
          </div>
        )}

        {error && <p className="text-danger">{error}</p>}

        {!loading && !error && details && (
          <>
            {filteredEntries.length === 0 && <p>{t('viewDetailsPopupTranslations.messages.noDetails')}</p>}

            <Row className="g-2">
              {filteredEntries.map(([key, value]) => (
                <Col md={4} key={key}>
                  <div className="details-card">
                    <div className="label">
                      {fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </div>
                    <div className="value">{renderFieldValue(key, value)}</div>
                  </div>
                </Col>

              ))}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('viewDetailsPopupTranslations.buttons.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewDetailsPopup;
